import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Truck } from 'lucide-react';

/**
 * Mobilisation Phase 2 — Transport Prompt
 *
 * Shown after a job is created with an asset type that has `requires_transport=true`.
 * Offers [Auto-create transport legs] or [Skip].
 */
const TransportPromptDialog = ({
  open,
  onOpenChange,
  api,
  jobId,
  jobData,               // { job_number, start_time, end_time, start_date, equipment_type }
  equipmentType,         // The primary equipment type doc (has transport_* fields)
  transportType,         // Looked-up transport equipment type doc
  onComplete,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [includeReturn, setIncludeReturn] = useState(
    Boolean(equipmentType?.transport_return_required)
  );

  const offsetMinutes = useMemo(
    () => Number(equipmentType?.transport_offset_minutes ?? -60),
    [equipmentType]
  );

  const toSiteTime = useMemo(() => {
    const startTime = jobData?.start_time || '07:00';
    return addMinutes(startTime, offsetMinutes);
  }, [jobData, offsetMinutes]);

  const fromSiteTime = useMemo(() => {
    const endTime = jobData?.end_time || '17:00';
    return addMinutes(endTime, Math.abs(offsetMinutes));
  }, [jobData, offsetMinutes]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/jobs/${jobId}/generate-transport`, {
        include_return: includeReturn,
      });
      toast.success(
        `Created ${res.data.jobs_created} transport leg${res.data.jobs_created > 1 ? 's' : ''}`
      );
      onComplete?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to create transport legs';
      toast.error(typeof detail === 'string' ? detail : 'Failed to create transport legs');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
  };

  if (!equipmentType || !transportType) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transport-prompt-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Transport required for {equipmentType.name}
          </DialogTitle>
          <DialogDescription>
            This asset type needs to be transported by a <strong>{transportType.name}</strong>.
            Auto-create transport leg{equipmentType?.transport_return_required ? 's' : ''}?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md border p-3 bg-muted/30">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Mobilisation (to site)</span>
              <span className="text-muted-foreground">
                {jobData?.start_date} @ {toSiteTime}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {transportType.name} — arrives {Math.abs(offsetMinutes)} min before Job #
              {jobData?.job_number || ''} starts
            </p>
          </div>

          <div className="flex items-start space-x-2 p-2">
            <Checkbox
              id="include_return"
              checked={includeReturn}
              onCheckedChange={setIncludeReturn}
              data-testid="include-return-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="include_return" className="text-sm font-medium">
                Also create return leg (Demobilisation)
              </Label>
              {includeReturn && (
                <p className="text-xs text-muted-foreground mt-1">
                  {transportType.name} — departs site at {fromSiteTime}
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={submitting}
            data-testid="transport-skip-btn"
          >
            Skip
          </Button>
          <Button
            onClick={handleCreate}
            disabled={submitting}
            data-testid="transport-create-btn"
            className="btn-volt"
          >
            {submitting ? 'Creating…' : 'Create transport leg'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function addMinutes(timeStr, minutes) {
  const [h, m] = (timeStr || '07:00').split(':').map(Number);
  let total = h * 60 + m + Number(minutes);
  total = Math.max(0, Math.min(total, 23 * 60 + 59));
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

export default TransportPromptDialog;
